var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, TableHead, styled, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanType, PlanTypeCategory, StatusPermission } from '../../enum';
import { formatNumberByLocale } from '../../util';
import { useUserPermissionsAPI } from '../app/selectors';
import { FormField, NativeNumberInput, Table, TableBody, TableCell, TableContainer, TableRow } from '../common';
import { useReportTimeline } from '../daily/selectors';
import { StyledFirstCell, StyledTableCell } from '../period/periodTable';
import { useReportAPI } from '../report/selectors';
import { formatTableHeaderReport } from '../report/util';
import { PLAN_COLORS } from '../wizard/constants';
import { MONTH_OF_YEAR, TimelineType, useEffectivePlanPermission, useIsPlanLocked } from '../wizard/selectors';
import { BOLD_KEYS, NON_DEFERRABLE_BOTTOM_KEY, PERCENTAGE_KEYS, TRAINING_KEY, TRAINING_KEY_BOTTOM, TRAINING_KEY_EDITABLE } from './constants';
import { TrainingColumn } from './enum';
export var TRAINING_STICKIES = ['25rem'];
var TRAINING_PERIOD_CELL_MIN_WIDTH = 112;
export var QueueNameTableCell = styled(StyledFirstCell)(function (_a) {
  var theme = _a.theme;
  return {
    borderLeft: 'unset !important',
    minWidth: theme.typography.pxToRem(300),
    top: 'initial',
    zIndex: 5
  };
});
export var TrainingStyledTableCell = styled(StyledTableCell)(function (_a) {
  var theme = _a.theme;
  return {
    position: 'relative',
    whiteSpace: 'nowrap',
    minWidth: theme.typography.pxToRem(TRAINING_PERIOD_CELL_MIN_WIDTH)
  };
});
var TrainingPeriodTableCell = styled(TableCell)(function (_a) {
  var theme = _a.theme;
  return {
    minWidth: theme.typography.pxToRem(TRAINING_PERIOD_CELL_MIN_WIDTH)
  };
});
export var TrainingTableHead = function (_a) {
  var _b;
  var queueName = _a.queueName,
    bgColor = _a.bgColor,
    disableWeekDate = _a.disableWeekDate,
    _c = _a.addColumns,
    addColumns = _c === void 0 ? [] : _c;
  var reportData = useReportAPI()[0];
  var isMonthly = ((_b = reportData.data) === null || _b === void 0 ? void 0 : _b.planType) === PlanType.Monthly;
  var timelines = useReportTimeline.apply(void 0, __spreadArray([true, true, !disableWeekDate], addColumns, false));
  var totalColumn = MONTH_OF_YEAR + addColumns.length;
  return _jsx(TableHead, {
    children: timelines.map(function (_a, rowIdx) {
      var titles = _a[0],
        type = _a[1];
      return _jsxs(TableRow, {
        children: [!rowIdx && _jsx(QueueNameTableCell, {
          stickySizes: TRAINING_STICKIES,
          value: rowIdx ? null : queueName,
          rowSpan: 2,
          color: bgColor
        }), titles === null || titles === void 0 ? void 0 : titles.map(function (title, cellIdx, arr) {
          return _jsx(TrainingStyledTableCell, {
            colSpan: type === TimelineType.Years ? totalColumn : 1,
            highlightBorder: isMonthly && cellIdx !== arr.length - 1 && (type === TimelineType.Years || !((cellIdx + 1) % totalColumn)),
            value: formatTableHeaderReport(title),
            color: bgColor
          }, "".concat(title, "-").concat(cellIdx));
        })]
      }, type);
    })
  });
};
var TrainingTableInputCell = function (_a) {
  var inputKey = _a.inputKey,
    trainingItem = _a.trainingItem,
    control = _a.control,
    setValue = _a.setValue,
    disabled = _a.disabled,
    isPercentage = _a.isPercentage,
    isAbsoluteMode = _a.isAbsoluteMode,
    onSyncSiblingField = _a.onSyncSiblingField;
  var getMinMaxValueInput = function (key) {
    var _a;
    var _b;
    var values = (_a = {}, _a[TrainingColumn.TrainingLength] = {
      min: 0,
      max: 24
    }, _a[TrainingColumn.ForecastedGraduationRate] = {
      min: 1,
      max: 100,
      decimal: 2
    }, _a[TrainingColumn.AdjustedAttritionRate] = {
      min: 0,
      max: 99.99
    }, _a[TrainingColumn.ForecastedAgentAttrition] = {
      min: 0,
      max: 9999,
      decimal: 2
    }, _a[TrainingColumn.ForecastAttritionRate] = {
      min: 0,
      max: 99.99
    }, _a);
    return (_b = values[key]) !== null && _b !== void 0 ? _b : {
      min: 1,
      max: 10000,
      decimal: undefined
    };
  };
  var _b = getMinMaxValueInput(inputKey),
    min = _b.min,
    max = _b.max,
    minMaxDecimal = _b.decimal;
  // Both percentage rate inputs (AdjustedAttritionRate and ForecastAttritionRate in absolute mode)
  // use 2 decimal places.
  var isAttritionRateKey = inputKey === TrainingColumn.AdjustedAttritionRate || isAbsoluteMode && inputKey === TrainingColumn.ForecastAttritionRate;
  var decimal = minMaxDecimal !== null && minMaxDecimal !== void 0 ? minMaxDecimal : isAttritionRateKey ? 2 : undefined;
  var handleChange = function (v) {
    if (onSyncSiblingField && typeof v === 'number') {
      onSyncSiblingField(v);
    }
  };
  return _jsx(FormField, __assign({
    disableHelperText: true,
    required: true,
    name: "trainings.".concat(trainingItem.month, ".").concat(inputKey),
    control: control
  }, {
    children: _jsx(NativeNumberInput, {
      disabled: disabled,
      min: min !== null && min !== void 0 ? min : 0,
      max: max !== null && max !== void 0 ? max : 10000,
      setValue: setValue,
      suffix: isPercentage ? '%' : '',
      decimal: decimal,
      sx: {
        paddingRight: isPercentage ? '1.25rem' : ''
      },
      onChange: onSyncSiblingField ? handleChange : undefined
    })
  }));
};
var TrainingTableDisplayCell = function (_a) {
  var inputKey = _a.inputKey,
    trainingItem = _a.trainingItem,
    isPercentage = _a.isPercentage,
    bold = _a.bold;
  var key = inputKey;
  var isNumberVal = typeof trainingItem[key] === 'number';
  var value = trainingItem[key];
  var getCellValue = function () {
    if (isPercentage && isNumberVal) {
      var percentage = Number(value) * 100;
      var digits = inputKey === TrainingColumn.ForecastAttritionRate ? 2 : 1;
      return "".concat(formatNumberByLocale(percentage, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }), "%");
    }
    if (inputKey === TrainingColumn.ForecatedAnswerSpeed && isNumberVal) {
      return formatNumberByLocale(Math.round(Number(value)), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
    if (isNumberVal) {
      return formatNumberByLocale(Number(value));
    }
    return value;
  };
  return _jsx(Box, __assign({
    sx: {
      fontWeight: bold ? 700 : 'normal'
    }
  }, {
    children: getCellValue()
  }));
};
var TrainingTableBody = function (_a) {
  var _b, _c;
  var trainings = _a.trainings,
    bgColor = _a.bgColor,
    control = _a.control,
    setValue = _a.setValue,
    disabled = _a.disabled,
    isDeferrableQueue = _a.isDeferrableQueue,
    isAbandonableQueue = _a.isAbandonableQueue,
    attritionEntryMode = _a.attritionEntryMode;
  var theme = useTheme();
  var t = useTranslation('report', {
    keyPrefix: 'training'
  }).t;
  var reportData = useReportAPI()[0];
  var isMonthly = ((_b = reportData.data) === null || _b === void 0 ? void 0 : _b.planType) === PlanType.Monthly;
  var isProdHoursPlan = ((_c = reportData.data) === null || _c === void 0 ? void 0 : _c.planTypeCategory) === PlanTypeCategory.ProdHours;
  var isAbsoluteMode = attritionEntryMode === 'absolute';
  // In absolute mode, reorder: move ForecastedAgentAttrition right after ForecastAttritionRate
  var keys = function () {
    var filteredKeys = TRAINING_KEY.filter(function (key) {
      if (isDeferrableQueue && NON_DEFERRABLE_BOTTOM_KEY.includes(key)) {
        return false;
      }
      if (isProdHoursPlan && NON_DEFERRABLE_BOTTOM_KEY.includes(key)) {
        return false;
      }
      if (!isAbandonableQueue && TrainingColumn.ForecastedAbandonmentRate === key) {
        return false;
      }
      return !!key;
    });
    return filteredKeys;
  }();
  var getSyncHandler = useCallback(function (key, cell) {
    var _a, _b, _c;
    var currentRSP = (_a = cell.rosteredStaffPlanned) !== null && _a !== void 0 ? _a : 0;
    var currentAdjRatePct = (_b = cell.adjustedAttritionRate) !== null && _b !== void 0 ? _b : 0;
    var forecastAttritionDecimal = (_c = cell.forecastAttritionRate) !== null && _c !== void 0 ? _c : 0;
    var currentScaleFactor = 1 - forecastAttritionDecimal + currentAdjRatePct / 100;
    var staffBase = currentScaleFactor > 0 ? currentRSP / currentScaleFactor : currentRSP;
    if (key === TrainingColumn.AdjustedAttritionRate) {
      return function (newRate) {
        if (newRate == null) return;
        var newScaleFactor = 1 - forecastAttritionDecimal + newRate / 100;
        var newRSP = Math.max(0, staffBase * newScaleFactor);
        var agents = Number((newRate / 100 * newRSP).toFixed(2));
        setValue("trainings.".concat(cell.month, ".forecastedAgentAttrition"), agents, {
          shouldDirty: true
        });
      };
    }
    if (key === TrainingColumn.ForecastedAgentAttrition) {
      return function (newAgents) {
        if (newAgents == null || staffBase <= 0) return;
        var oneMinusAttrition = 1 - forecastAttritionDecimal;
        var discriminant = oneMinusAttrition * oneMinusAttrition + 4 * newAgents / staffBase;
        if (discriminant < 0) return;
        var x = (-oneMinusAttrition + Math.sqrt(discriminant)) / 2;
        var rate = Math.round((x * 100 + Number.EPSILON) * 100) / 100;
        setValue("trainings.".concat(cell.month, ".adjustedAttritionRate"), rate, {
          shouldDirty: true
        });
      };
    }
    return undefined;
  }, [setValue]);
  return _jsx(TableBody, {
    children: keys.map(function (key, index) {
      var isBottomRow = TRAINING_KEY_BOTTOM.includes(key);
      // In absolute mode, ForecastAttritionRate and ForecastedAgentAttrition are also editable
      var isEditRow = TRAINING_KEY_EDITABLE.includes(key);
      // In absolute mode:
      // - ForecastAttritionRate stays as percentage (calculated as absolute/staff)
      // - ForecastedAgentAttrition displays up to 2 decimal places (not %)
      // - AdjustedAttritionRate stays as percentage (calculator now provides it as %)
      var isPercentage = isAbsoluteMode && key === TrainingColumn.ForecastedAgentAttrition ? false : PERCENTAGE_KEYS.includes(key);
      var isBold = BOLD_KEYS.includes(key);
      return _jsxs(TableRow, {
        children: [_jsx(TableCell, {
          stickySizes: TRAINING_STICKIES,
          color: isBottomRow ? bgColor[1] : theme.palette.background.default,
          component: "th",
          align: "left",
          value: t(key, {
            defaultValue: key
          })
        }), trainings.map(function (cell, cellIdx) {
          return _jsx(TrainingPeriodTableCell, __assign({
            color: isBottomRow || isEditRow ? bgColor[1] : theme.palette.common.white,
            highlightBorder: isMonthly && !((cellIdx + 1) % 12) && trainings.length - 1 !== cellIdx
          }, {
            children: isEditRow ? _jsx(TrainingTableInputCell, {
              disabled: disabled,
              inputKey: key,
              trainingItem: cell,
              setValue: setValue,
              control: control,
              isPercentage: isPercentage,
              isAbsoluteMode: isAbsoluteMode,
              onSyncSiblingField: getSyncHandler(key, cell)
            }) : _jsx(TrainingTableDisplayCell, {
              inputKey: key,
              trainingItem: cell,
              isPercentage: isPercentage,
              bold: isBold
            })
          }), "".concat(cell.id).concat(index).concat(cellIdx));
        })]
      }, key);
    })
  });
};
export var TrainingTable = function (_a) {
  var currentScenario = _a.currentScenario,
    trainings = _a.trainings,
    control = _a.control,
    setValue = _a.setValue,
    isDeferrableQueue = _a.isDeferrableQueue,
    isAbandonableQueue = _a.isAbandonableQueue,
    attritionEntryMode = _a.attritionEntryMode;
  var _b = currentScenario.colorIndex,
    colorIndex = _b === void 0 ? 0 : _b;
  var recruitmentPermission = useUserPermissionsAPI().recruitmentPermission;
  var effectiveRecruitmentPermission = useEffectivePlanPermission(recruitmentPermission);
  var t = useTranslation('translation').t;
  var isPlanLocked = useIsPlanLocked();
  return _jsx(TableContainer, __assign({
    topScroll: true
  }, {
    children: _jsxs(Table, {
      children: [_jsx(TrainingTableHead, {
        queueName: currentScenario.isBaseline ? t('baseline') : currentScenario.name,
        bgColor: PLAN_COLORS[colorIndex][0]
      }), _jsx(TrainingTableBody, {
        trainings: trainings,
        bgColor: PLAN_COLORS[colorIndex],
        control: control,
        setValue: setValue,
        disabled: effectiveRecruitmentPermission !== StatusPermission.Edit || isPlanLocked,
        isDeferrableQueue: isDeferrableQueue,
        isAbandonableQueue: isAbandonableQueue,
        attritionEntryMode: attritionEntryMode
      })]
    })
  }));
};